import React, { useState, useEffect } from 'react';
import { LogIn, UserPlus, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../components/layout/SEO';

const Login: React.FC = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get priceId from URL params once at the component level
    const urlPriceId = searchParams.get('priceId');

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) {
                console.log('Login: Sessão ativa detectada, movendo para Dashboard...');
                navigate('/dashboard', { replace: true });
            }
        });
    }, [navigate]);

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError(null);
        try {
            // Use the component-level urlPriceId instead of re-fetching or shadowing
            if (urlPriceId && urlPriceId.startsWith('price_')) {
                console.log('Salvando intenção de compra no localStorage:', urlPriceId);
                localStorage.setItem('pending_purchase_price_id', urlPriceId);
            }

            // PASSO 2: Construir URL de retorno dinâmica
            const redirectUrl = urlPriceId
                ? `${window.location.origin}/dashboard?checkoutPrice=${urlPriceId}`
                : `${window.location.origin}/dashboard`;

            const { error: authError } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: redirectUrl
                }
            });
            if (authError) throw authError;
        } catch (err: any) {
            setError(err.message || 'Erro ao conectar com Google');
            setLoading(false);
        }
    };

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isLogin && !acceptedTerms) {
            setError('Você precisa aceitar os Termos e a Política de Privacidade.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            if (isLogin) {
                const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
                if (authError) throw authError;

                // O checkout agora é tratado GLOBALMENTE no App.tsx
                navigate('/dashboard', { replace: true });
            } else {
                const { error: authError } = await supabase.auth.signUp({
                    email,
                    password,
                    options: {
                        data: {
                            pending_plan: urlPriceId // Use urlPriceId clearly
                        }
                    }
                });
                if (authError) throw authError;
                alert('Confirme seu e-mail para ativar a conta!');

                navigate('/dashboard', { replace: true });
            }
        } catch (err: any) {
            setError(err.message || 'Erro ao processar autenticação');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4 font-manrope text-white">
            <SEO title={isLogin ? "Entrar" : "Criar Conta"} description="Acesse sua conta Gestão MEI ou cadastre-se para automatizar seu livro caixa e faturamento." />
            
            <div className="w-full max-w-md bg-white/5 border border-white/10 p-8 rounded-xl backdrop-blur-sm">
                <div className="flex justify-center mb-8">
                    <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center shadow-lg shadow-primary/20 text-white font-bold text-2xl">
                        MEI
                    </div>
                </div>

                <h2 className="text-3xl font-extrabold text-center mb-2">
                    {isLogin ? 'Bem-vindo de volta' : 'Crie sua conta'}
                </h2>
                <p className="text-white/60 text-center mb-8">
                    {isLogin ? 'Gerencie suas finanças com facilidade' : 'Comece a organizar seu negócio hoje'}
                </p>

                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm font-semibold text-center">
                        {error}
                    </div>
                )}

                <button
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    className="w-full bg-white hover:bg-white/90 disabled:opacity-50 text-black font-bold py-4 rounded-xl shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-3 mb-6"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                            fill="currentColor"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                            fill="#EA4335"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-1 .67-2.28 1.07-3.71 1.07-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                            fill="#FBBC05"
                            d="M5.84 14.11c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.09H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.91l3.66-2.8z"
                        />
                        <path
                            fill="#4285F4"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.09l3.66 2.84c.87-2.6 3.3-4.55 6.16-4.55z"
                        />
                        <path fill="none" d="M1 1h22v22H1z" />
                    </svg>
                    Entrar com Google
                </button>

                <div className="relative mb-8">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/10"></div>
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-[#0a0a0a] px-4 text-white/40 font-bold tracking-widest">
                            ou continue com e-mail
                        </span>
                    </div>
                </div>

                <form className="space-y-6" onSubmit={handleAuth}>
                    <div>
                        <label className="block text-sm font-semibold mb-2 text-white/80">E-mail</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-primary transition-colors text-white"
                            placeholder="seu@email.com"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-2 text-white/80">Senha</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-primary transition-colors text-white"
                            placeholder="••••••••"
                            required
                        />
                    </div>

                    {!isLogin && (
                        <div className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                id="terms"
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                className="mt-1 w-4 h-4 rounded border-white/10 bg-white/5 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                required
                            />
                            <label htmlFor="terms" className="text-xs text-white/60 leading-tight cursor-pointer">
                                Li e aceito os{' '}
                                <a href="/termos" target="_blank" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>Termos de Uso</a>{' '}
                                e a{' '}
                                <a href="/privacidade" target="_blank" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>Política de Privacidade</a>.
                            </label>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold py-4 rounded-xl shadow-lg shadow-primary/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <Loader2 className="animate-spin" size={20} />
                        ) : isLogin ? (
                            <>
                                <LogIn size={20} />
                                Entrar
                            </>
                        ) : (
                            <>
                                <UserPlus size={20} />
                                Cadastrar
                            </>
                        )}
                    </button>
                </form>

                <div className="mt-8 text-center">
                    <button
                        onClick={() => setIsLogin(!isLogin)}
                        className="text-primary hover:text-primary/80 text-sm font-semibold transition-colors disabled:opacity-50"
                        disabled={loading}
                    >
                        {isLogin ? 'Não tem uma conta? Cadastre-se' : 'Já tem uma conta? Faça login'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Login;
